import {injectable} from 'inversify';
import {makeAutoObservable, observable} from 'mobx';

import type {MakeOptional} from '../../../../utils/utility-types';
import type {ModalController} from '../ModalController/Modal.controller';

import {definedOnly} from '../../../../utils/object/definedOnly';
import {MODAL_OFFSET, MODAL_PADDING} from '../../constants';
import {setAppElement} from './setAppElement';

@injectable()
export class ModalService {
  config: ModalService.Config = {
    ariaHideSelector: 'root',
    modalOffset: MODAL_OFFSET,
    modalPadding: MODAL_PADDING,
    mountElementSelector: 'root',
    overlayPosition: 'fixed',
  };
  presentedModals = observable.map(new Map<string, ModalController>());

  constructor() {
    makeAutoObservable(this, undefined, {
      autoBind: true,
    });
  }

  init(config: ModalService.InitialConfig) {
    this.config = {...this.config, ...definedOnly(config)};

    const appElement: HTMLElement =
      document.querySelector(this.config.ariaHideSelector) ?? document.body;

    setAppElement(appElement);
  }

  get isAnyModalOpen() {
    return this.presentedModals.size > 0;
  }

  closeAll() {
    this.presentedModals.forEach(controller => controller.hide());
  }

  register(controller: ModalController) {
    this.presentedModals.set(controller.id, controller);
  }

  unregister(controller: ModalController) {
    this.presentedModals.delete(controller.id);
  }
}

export namespace ModalService {
  export type Config = {
    ariaHideSelector: string;
    modalOffset: number;
    modalPadding: number;
    mountElementSelector: string;
    overlayBlur?: boolean | number;
    overlayPosition?: 'absolute' | 'fixed';
    renderCloseIcon?: () => React.ReactNode;
  };

  export type InitialConfig = MakeOptional<
    Config,
    'modalOffset' | 'modalPadding' | 'overlayPosition' | 'renderCloseIcon'
  >;
}
